import {
  ShieldCheck,
  FlaskConical,
  ScrollText,
  UserCheck,
  DollarSign,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Check,
  X,
  Lightbulb,
} from "lucide-react";
import type { HiveWorkflow } from "@/types";
import { useWorkflow, validateWorkflow, permissionFootprint } from "@/store/workflowStore";
import { useExecution } from "@/store/executionStore";
import { useHive } from "@/store/useHive";
import { WorkflowValidationPanel } from "./WorkflowValidationPanel";
import { WorkflowTestRunPanel } from "./WorkflowTestRunPanel";
import { Pill, RiskBadge, EXEC_STATUS_META } from "@/components/hive/shared";
import type { DrawerTab } from "@/store/workflowStore";

const TABS: { id: DrawerTab; label: string; icon: typeof ShieldCheck }[] = [
  { id: "validation", label: "Validation", icon: ShieldCheck },
  { id: "test", label: "Test Run", icon: FlaskConical },
  { id: "logs", label: "Execution Logs", icon: ScrollText },
  { id: "approvals", label: "Approvals", icon: UserCheck },
  { id: "cost", label: "Cost & Risk", icon: DollarSign },
];

export function WorkflowBottomDrawer({ wf }: { wf: HiveWorkflow }) {
  const open = useWorkflow((s) => s.drawerOpen);
  const setDrawer = useWorkflow((s) => s.setDrawer);
  const tab = useWorkflow((s) => s.drawerTab);
  const setTab = useWorkflow((s) => s.setDrawerTab);

  const issues = validateWorkflow(wf);
  const errors = issues.filter((i) => i.level === "error").length;

  const approvals = useExecution((s) => s.approvals).filter((a) => a.workflowName === wf.name);
  const pendingApprovals = approvals.filter((a) => a.status === "pending").length;

  return (
    <div className="flex shrink-0 flex-col border-t border-app bg-surface/40 backdrop-blur-xl" style={{ height: open ? 248 : 40 }}>
      <div className="flex h-10 shrink-0 items-center gap-1 px-2">
        {TABS.map((t) => {
          const active = t.id === tab && open;
          const badge =
            t.id === "validation" && errors > 0 ? errors : t.id === "approvals" && pendingApprovals > 0 ? pendingApprovals : null;
          return (
            <button
              key={t.id}
              onClick={() => {
                if (t.id === tab && open) setDrawer(false);
                else setTab(t.id);
              }}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium transition ${
                active ? "bg-surface-2 text-app" : "text-app-2 hover:text-app-1"
              }`}
            >
              <t.icon size={13} />
              {t.label}
              {badge != null && (
                <span className="grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">{badge}</span>
              )}
            </button>
          );
        })}
        <button onClick={() => setDrawer(!open)} className="ml-auto grid h-7 w-7 place-items-center rounded-lg text-app-2 transition hover:bg-surface-2 hover:text-app">
          {open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
        </button>
      </div>

      {open && (
        <div className="min-h-0 flex-1 overflow-hidden">
          {tab === "validation" && <WorkflowValidationPanel wf={wf} />}
          {tab === "test" && <WorkflowTestRunPanel wf={wf} />}
          {tab === "logs" && <LogsTab wf={wf} />}
          {tab === "approvals" && <ApprovalsTab wf={wf} />}
          {tab === "cost" && <CostTab wf={wf} />}
        </div>
      )}
    </div>
  );
}

/* ── Logs ─────────────────────────────────────────────────────────────────── */

function LogsTab({ wf }: { wf: HiveWorkflow }) {
  const executions = useExecution((s) => s.executions).filter((e) => e.workflowId === wf.id);
  const openApp = useHive((s) => s.openApp);
  const selectExecution = useExecution((s) => s.selectExecution);
  const setWorkflowFilter = useExecution((s) => s.setWorkflowFilter);

  const openInCenter = (id?: string) => {
    setWorkflowFilter(wf.id);
    if (id) selectExecution(id);
    openApp("execution");
  };

  if (executions.length === 0)
    return <Empty text="No runs yet — run a sandbox test or publish to start executing." />;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-app px-4 py-2">
        <span className="text-[11px] text-app-2">{executions.length} recent runs</span>
        <button onClick={() => openInCenter()} className="flex items-center gap-1 text-[11px] font-medium text-cyan-400 hover:underline">
          Open in Execution Center <ExternalLink size={11} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar p-2">
        {executions.map((e) => {
          const m = EXEC_STATUS_META[e.status];
          const failed = e.steps.find((s) => s.status === "failed");
          return (
            <button key={e.id} onClick={() => openInCenter(e.id)} className="group flex w-full items-center gap-3 rounded-lg border border-app bg-surface/40 px-3 py-2 text-left transition hover:border-app-strong mb-1.5">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: m.color }} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-app">{e.name}</div>
                <div className="text-[10px] text-app-2">{e.startedAt} · {e.duration}{failed ? ` · failed at “${failed.label}”` : ""}</div>
              </div>
              <Pill color={m.color}>{m.label}</Pill>
              <ExternalLink size={12} className="text-app-2 opacity-0 transition group-hover:opacity-100" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Approvals ────────────────────────────────────────────────────────────── */

function ApprovalsTab({ wf }: { wf: HiveWorkflow }) {
  const approvals = useExecution((s) => s.approvals).filter((a) => a.workflowName === wf.name);
  const approve = useExecution((s) => s.approve);
  const reject = useExecution((s) => s.reject);
  const pending = approvals.filter((a) => a.status === "pending");
  const history = approvals.filter((a) => a.status !== "pending");

  if (approvals.length === 0) return <Empty text="No approval requests for this workflow." />;

  return (
    <div className="flex h-full flex-col overflow-y-auto no-scrollbar p-3">
      {pending.length > 0 && <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-app-2">Pending · {pending.length}</div>}
      {pending.map((a) => (
        <div key={a.id} className="mb-1.5 rounded-lg border border-app bg-surface/40 p-2.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-app">{a.action}</span>
            <RiskBadge risk={a.risk} />
            <span className="ml-auto text-[10px] text-app-2">{a.timeout}</span>
          </div>
          <div className="mt-1 line-clamp-2 text-[11px] text-app-2">{a.preview}</div>
          <div className="mt-2 flex items-center gap-1.5">
            <button onClick={() => approve(a.id)} className="flex items-center gap-1 rounded-md bg-emerald-500/15 px-2.5 py-1 text-[11px] font-medium text-emerald-500 transition hover:bg-emerald-500/25"><Check size={12} /> Approve</button>
            <button onClick={() => reject(a.id, "")} className="flex items-center gap-1 rounded-md bg-rose-500/15 px-2.5 py-1 text-[11px] font-medium text-rose-500 transition hover:bg-rose-500/25"><X size={12} /> Reject</button>
          </div>
        </div>
      ))}
      {history.length > 0 && <div className="mb-1.5 mt-2 text-[10px] font-semibold uppercase tracking-wider text-app-2">History</div>}
      {history.map((a) => (
        <div key={a.id} className="mb-1 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]">
          <span className={`h-1.5 w-1.5 rounded-full ${a.status === "approved" ? "bg-emerald-500" : "bg-rose-500"}`} />
          <span className="text-app-1">{a.action}</span>
          <span className="ml-auto capitalize text-app-2">{a.status}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Cost & Risk ──────────────────────────────────────────────────────────── */

function CostTab({ wf }: { wf: HiveWorkflow }) {
  const fp = permissionFootprint(wf);
  const total = wf.cost.model + wf.cost.cloud + wf.cost.verification;
  const bars = [
    { label: "Model", value: wf.cost.model, color: "#a855f7" },
    { label: "Cloud workspace", value: wf.cost.cloud, color: "#14b8a6" },
    { label: "Verification", value: wf.cost.verification, color: "#22c55e" },
  ];
  const max = Math.max(...bars.map((b) => b.value), 0.01);
  const guardrails: string[] = [];
  if (fp.deploy) guardrails.push("Require admin approval for production deploys");
  if (fp.external) guardrails.push("Disable external posting in sandbox");
  if (fp.budget) guardrails.push(`Cap cost per run at $${wf.controls.maxCostPerRun}`);
  guardrails.push(`Pause after repeated failures`);

  return (
    <div className="grid h-full grid-cols-3 gap-3 overflow-y-auto no-scrollbar p-3">
      <div className="rounded-xl border border-app bg-surface/40 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-app-2">Estimated cost / run</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-app">${total.toFixed(2)}</div>
        <div className="text-[11px] text-emerald-500">BYONC saves ~${wf.cost.byoncSavings.toFixed(2)} · est. {wf.cost.estRuntime}</div>
        <div className="mt-3 space-y-2">
          {bars.map((b) => (
            <div key={b.label}>
              <div className="mb-1 flex items-center justify-between text-[10px] text-app-2">
                <span>{b.label}</span><span className="tabular-nums">${b.value.toFixed(2)}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
                <div className="h-full rounded-full" style={{ width: `${(b.value / max) * 100}%`, background: b.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-app bg-surface/40 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-app-2">Risk & footprint</div>
        <div className="mt-1.5 flex items-center gap-2"><RiskBadge risk={wf.risk} /><span className="text-[11px] text-app-2">workflow risk</span></div>
        <div className="mt-3 space-y-1.5 text-[11px]">
          <Cap label="Reads from" on={fp.reads.length > 0} detail={`${fp.reads.length} scopes`} />
          <Cap label="Writes to" on={fp.writes.length > 0} detail={`${fp.writes.length} scopes`} />
          <Cap label="Modifies code" on={fp.code} />
          <Cap label="Creates PRs" on={fp.pr} />
          <Cap label="Posts externally" on={fp.external} />
          <Cap label="Triggers deploys" on={fp.deploy} />
          <Cap label="Spends budget" on={fp.budget} />
        </div>
      </div>

      <div className="rounded-xl border border-app bg-surface/40 p-3">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-app-2"><Lightbulb size={12} /> Recommended guardrails</div>
        <div className="mt-2 space-y-1.5">
          {guardrails.map((g) => (
            <div key={g} className="flex items-start gap-1.5 rounded-lg border border-app bg-surface-2/40 px-2.5 py-1.5 text-[11px] text-app-1">
              <ShieldCheck size={12} className="mt-0.5 shrink-0 text-emerald-500" /> {g}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Cap({ label, on, detail }: { label: string; on: boolean; detail?: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-app-1">{label}</span>
      <span className={on ? "font-medium text-amber-500" : "text-app-2"}>{on ? detail ?? "Yes" : "No"}</span>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="grid h-full place-items-center px-6 text-center text-xs text-app-2">{text}</div>;
}
